import React, { useEffect, useState } from "react";
import useToken from "../../../../hooks/auth/useToken";
import {
  withdrawalRequestService,
  WithdrawalRequest,
  WithdrawalRequestStatus,
} from "../../../../services/administration/withdrawalRequestService";

import { Loader2, Inbox, CheckCircle, XCircle, AlertCircle, Clock } from "lucide-react";

type TabStatus = "PENDING" | "PROCESSED";

const statusLabel = (status: WithdrawalRequestStatus) => {
  switch (status) {
    case "PENDING":
      return "En attente";
    case "SUCCESS":
      return "Traitée";
    case "REJECTED":
      return "Refusée";
    case "CANCELLED":
      return "Annulée";
    default:
      return status;
  }
};

const statusBadgeClass = (status: WithdrawalRequestStatus) => {
  switch (status) {
    case "PENDING":
      return "bg-yellow-100 text-yellow-700";
    case "SUCCESS":
      return "bg-green-100 text-green-700";
    case "REJECTED":
      return "bg-red-100 text-red-700";
    case "CANCELLED":
      return "bg-gray-100 text-gray-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const WithdrawalRequestsPage: React.FC = () => {
  const { token } = useToken();
  const [activeTab, setActiveTab] = useState<TabStatus>("PENDING");
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState<string>("");

  const fetchRequests = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const status = activeTab === "PENDING" ? "PENDING" : "SUCCESS";
      const data = await withdrawalRequestService.listRequests(token, { status });
      setRequests(data.items);
    } catch (error) {
      console.error("Erreur lors du chargement des demandes de retrait:", error);
      alert("Impossible de charger les demandes de retrait.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token]);

  const handleStatusChange = async (request: WithdrawalRequest, newStatus: WithdrawalRequestStatus) => {
    if (!token) return;
    if (!confirm(`Voulez-vous vraiment marquer cette demande comme « ${statusLabel(newStatus)} » ?`)) return;

    setActionLoadingId(request.id);
    try {
      await withdrawalRequestService.updateStatus(token, request.id, newStatus, adminNote || undefined);
      alert(`Demande mise à jour avec succès.`);
      setAdminNote("");
      await fetchRequests();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      alert("Erreur lors de la mise à jour du statut.");
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Inbox className="w-8 h-8 text-green-600" />
          Demandes de retrait
        </h1>
        <p className="text-gray-600">Gérez les demandes de retrait bancaire des utilisateurs.</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab("PENDING")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
              activeTab === "PENDING"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <Clock className="w-5 h-5" />
            En attente
          </button>
          <button
            onClick={() => setActiveTab("PROCESSED")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
              activeTab === "PROCESSED"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <CheckCircle className="w-5 h-5" />
            Déjà traitées
          </button>
        </div>
      </div>

      {/* Admin note input for pending actions */}
      {activeTab === "PENDING" && (
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
          <label className="block mb-2 text-sm font-medium text-gray-700">Note administrative (optionnelle)</label>
          <input
            type="text"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="Note à joindre à l'email utilisateur en cas de refus..."
            className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      )}

      {/* Requests list */}
      <div className="bg-white rounded-lg border border-gray-200">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-12 h-12 text-green-600 animate-spin" />
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <AlertCircle className="w-12 h-12 mb-4" />
            <p className="text-lg font-medium">Aucune demande {activeTab === "PENDING" ? "en attente" : "traitée"}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {requests.map((request) => (
              <div key={request.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {request.amount.toLocaleString()} {request.currency}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadgeClass(request.status)}`}
                      >
                        {statusLabel(request.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">Référence : {request.id}</p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Méthode :</span> {request.withdrawal_method}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Identifiant :</span> {request.withdrawal_identifier}
                      {request.operator && ` (${request.operator})`}
                    </p>
                    {request.fee_amount !== null && (
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Frais :</span> {request.fee_amount.toLocaleString()}{" "}
                        {request.currency}
                      </p>
                    )}
                    {request.total_amount !== null && (
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Total débité :</span> {request.total_amount.toLocaleString()}{" "}
                        {request.currency}
                      </p>
                    )}
                    {request.description && (
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Note utilisateur :</span> {request.description}
                      </p>
                    )}
                    {request.admin_note && (
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Note admin :</span> {request.admin_note}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      Demandé le {new Date(request.created_at).toLocaleString("fr-CA")}
                    </p>
                  </div>

                  {activeTab === "PENDING" && (
                    <div className="flex flex-col sm:flex-row gap-2 lg:justify-end">
                      <button
                        onClick={() => handleStatusChange(request, "SUCCESS")}
                        disabled={actionLoadingId === request.id}
                        className="flex gap-2 items-center px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {actionLoadingId === request.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        Marquer traitée
                      </button>
                      <button
                        onClick={() => handleStatusChange(request, "REJECTED")}
                        disabled={actionLoadingId === request.id}
                        className="flex gap-2 items-center px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        <XCircle className="w-4 h-4" />
                        Refuser
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default WithdrawalRequestsPage;
